import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { materialIconGlyph } from '../../lib/materialIcons'
import { buildSlotStatuses } from '../../lib/mealLogCore'
import { buildDailySummaryFromMeals, loadMealLogsForDate } from '../../lib/mealLog'
import { getCalendarDateForDayId, getTodayDayId, type DayId } from '../../lib/days'
import { KILO } from '../../constants/KILO_THEME'
import type { DailyNutritionSummary, MealLogRow } from '../../lib/mealLogCore'
import type { MealBlock } from '../../lib/protocolTypes'

type Segment = 'Today' | 'Week' | 'Month'

type TrackingProgressScreenProps = {
  open: boolean
  onClose: () => void
  todaySummary: DailyNutritionSummary | null
  weekTargetKcal: number
  weekActualKcal: number
  exercisesCompleted: number
  exercisesTotal: number
  trainingStreakDays: number
  userId?: string | null
  meals?: MealBlock[]
}

function dayIdForDate(date: Date): DayId {
  const map: DayId[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  return map[date.getDay()] ?? 'monday'
}

function OutlinedIcon({ name, className = '' }: { name: string; className?: string }) {
  return (
    <span className={`material-symbols-outlined ${className}`} aria-hidden>
      {materialIconGlyph(name)}
    </span>
  )
}

function SectionLabel({ children }: { children: string }) {
  return (
    <div className={`px-6 pb-3 pt-7 text-[11px] font-semibold uppercase tracking-[0.6px] ${KILO.text.soft}`}>
      {children}
    </div>
  )
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const onChange = () => setReduced(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return reduced
}

function AdherenceRing({ pct, reducedMotion }: { pct: number; reducedMotion: boolean }) {
  const clamped = Math.min(Math.max(pct, 0), 1)
  const transition = reducedMotion ? 'none' : 'stroke-dashoffset 1s cubic-bezier(0.4,0,0.2,1)'

  return (
    <div className="relative h-[80px] w-[80px] flex-shrink-0">
      <svg
        width={80}
        height={80}
        viewBox="0 0 80 80"
        style={{ transform: 'rotate(-90deg)', overflow: 'visible' }}
      >
        <circle cx={40} cy={40} r={35} fill="none" stroke="var(--kilo-nested)" strokeWidth={6} />
        <circle
          cx={40}
          cy={40}
          r={35}
          fill="none"
          stroke="var(--kilo-accent)"
          strokeWidth={6}
          strokeLinecap="round"
          strokeDasharray={220}
          strokeDashoffset={220 * (1 - clamped)}
          style={{
            filter: 'drop-shadow(0 0 6px rgba(0,200,83,0.5))',
            transition,
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-[18px] font-bold leading-none tracking-[-0.5px] ${KILO.text.primary}`}>
          {Math.round(clamped * 100)}%
        </span>
        <span className={`mt-0.5 text-[9px] font-medium uppercase tracking-[0.5px] ${KILO.text.soft}`}>of goal</span>
      </div>
    </div>
  )
}

function StreakRing({ pct, reducedMotion }: { pct: number; reducedMotion: boolean }) {
  const clamped = Math.min(Math.max(pct, 0), 1)
  const transition = reducedMotion ? 'none' : 'stroke-dashoffset 1s cubic-bezier(0.4,0,0.2,1)'

  return (
    <svg width="56" height="56" viewBox="0 0 56 56" style={{ transform: 'rotate(-90deg)' }}>
      <circle cx="28" cy="28" r="24" fill="none" stroke="var(--kilo-nested)" strokeWidth="4" />
      <circle
        cx="28"
        cy="28"
        r="24"
        fill="none"
        stroke="var(--kilo-accent)"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray="150"
        strokeDashoffset={150 * (1 - clamped)}
        style={{ filter: 'drop-shadow(0 0 4px rgba(0,200,83,0.4))', transition }}
      />
    </svg>
  )
}

function mapMealSlots(dayMeals: MealBlock[], logs: MealLogRow[]) {
  const blocks = dayMeals.map((m) => m.block_name)
  const statuses = buildSlotStatuses(logs, blocks)

  const isDone = (match: (name: string) => boolean) =>
    statuses.some(
      (s) => match(s.meal_block_name.toLowerCase()) && (s.intake === 'on_plan' || s.intake === 'substituted'),
    )

  return [
    { label: 'B', done: isDone((n) => n.includes('breakfast')) },
    { label: 'L', done: isDone((n) => n.includes('lunch')) },
    { label: 'S', done: isDone((n) => n.includes('snack')) },
    { label: 'D', done: isDone((n) => n.includes('dinner')) },
  ]
}

const TrackingProgressScreen = ({
  open,
  onClose,
  todaySummary,
  weekTargetKcal,
  weekActualKcal,
  exercisesCompleted,
  exercisesTotal,
  trainingStreakDays,
  userId,
  meals = [],
}: TrackingProgressScreenProps) => {
  const reducedMotion = usePrefersReducedMotion()
  const [mounted, setMounted] = useState(open)
  const [visible, setVisible] = useState(false)
  const [segment, setSegment] = useState<Segment>('Today')
  const [todayLogs, setTodayLogs] = useState<Awaited<ReturnType<typeof loadMealLogsForDate>>['logs']>([])
  const [streakDays, setStreakDays] = useState(trainingStreakDays)
  const [daysOnTarget, setDaysOnTarget] = useState(0)

  useEffect(() => {
    if (open) {
      setMounted(true)
      setVisible(false)
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true))
      })
      return () => cancelAnimationFrame(id)
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), 350)
    return () => window.clearTimeout(timer)
  }, [open])

  useEffect(() => {
    if (!mounted) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [mounted])

  useEffect(() => {
    if (!open || !userId) {
      setTodayLogs([])
      return
    }
    const todayId = getTodayDayId()
    const todayDate = getCalendarDateForDayId(todayId)
    let cancelled = false
    void loadMealLogsForDate(userId, todayDate).then(({ logs }) => {
      if (!cancelled) setTodayLogs(logs)
    })
    return () => {
      cancelled = true
    }
  }, [open, userId, todaySummary?.log_date])

  useEffect(() => {
    if (!open || !userId || meals.length === 0) {
      setStreakDays(trainingStreakDays)
      setDaysOnTarget(trainingStreakDays > 0 ? 1 : 0)
      return
    }

    let cancelled = false

    void (async () => {
      let consecutive = 0
      let onTargetInStreak = 0

      for (let i = 0; i < 7; i++) {
        const date = new Date()
        date.setHours(12, 0, 0, 0)
        date.setDate(date.getDate() - i)
        const dateStr = date.toISOString().slice(0, 10)
        const dayId = dayIdForDate(date)
        const dayMeals = meals.filter((m) => m.day_id === dayId)
        if (dayMeals.length === 0) break

        const { logs } = await loadMealLogsForDate(userId, dateStr)
        if (cancelled) return

        const summary = buildDailySummaryFromMeals(dateStr, dayMeals, logs)
        const onTarget = summary.adherence_pct != null && summary.adherence_pct >= 80

        if (i === 0 || consecutive > 0) {
          if (onTarget || (i === 0 && summary.slots_logged > 0)) {
            consecutive++
            if (onTarget) onTargetInStreak++
          } else if (i > 0) {
            break
          }
        } else {
          break
        }
      }

      if (!cancelled) {
        setStreakDays(Math.max(consecutive, trainingStreakDays > 0 ? 1 : 0))
        setDaysOnTarget(onTargetInStreak)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [open, userId, meals, trainingStreakDays])

  const todayId = getTodayDayId()
  const todayMeals = useMemo(() => meals.filter((m) => m.day_id === todayId), [meals, todayId])

  const segmentSummary = useMemo(() => {
    if (segment === 'Today') {
      return todaySummary
    }
    if (segment === 'Week') {
      return {
        planned: { calories_kcal: weekTargetKcal, protein_g: 0, carbs_g: 0, fat_g: 0 },
        actual: { calories_kcal: weekActualKcal, protein_g: 0, carbs_g: 0, fat_g: 0 },
        adherence_pct:
          weekTargetKcal > 0 ? Math.min(100, Math.round((weekActualKcal / weekTargetKcal) * 100)) : null,
      }
    }
    // TODO: wire to real month rollup when available
    return {
      planned: { calories_kcal: weekTargetKcal * 4, protein_g: 0, carbs_g: 0, fat_g: 0 },
      actual: { calories_kcal: weekActualKcal * 4, protein_g: 0, carbs_g: 0, fat_g: 0 },
      adherence_pct:
        weekTargetKcal > 0
          ? Math.min(100, Math.round(((weekActualKcal * 4) / (weekTargetKcal * 4)) * 100))
          : null,
    }
  }, [segment, todaySummary, weekTargetKcal, weekActualKcal])

  const calories = useMemo(
    () => ({
      consumed: segmentSummary?.actual.calories_kcal ?? 0,
      target: Math.max(segmentSummary?.planned.calories_kcal ?? 0, 1),
    }),
    [segmentSummary],
  )

  const adherencePct = useMemo(() => {
    if (segment === 'Today' && todaySummary?.adherence_pct != null) {
      return Math.min(todaySummary.adherence_pct / 100, 1)
    }
    if (segmentSummary?.adherence_pct != null) {
      return Math.min(segmentSummary.adherence_pct / 100, 1)
    }
    return Math.min(calories.consumed / calories.target, 1)
  }, [segment, todaySummary, segmentSummary, calories])

  const macros = useMemo(() => {
    const planned = todaySummary?.planned ?? { protein_g: 0, carbs_g: 0, fat_g: 0 }
    const actual = todaySummary?.actual ?? { protein_g: 0, carbs_g: 0, fat_g: 0 }
    return [
      {
        name: 'Protein',
        icon: 'egg',
        consumed: actual.protein_g,
        target: Math.max(planned.protein_g, 1),
        colorClass: 'text-kilo-accent',
        bgClass: 'bg-kilo-accent/10',
        fillClass: 'bg-kilo-accent',
      },
      {
        name: 'Carbohydrates',
        icon: 'grain',
        consumed: actual.carbs_g,
        target: Math.max(planned.carbs_g, 1),
        colorClass: 'text-[#4D9EFF]',
        bgClass: 'bg-[rgba(77,158,255,0.12)]',
        fillClass: 'bg-[#4D9EFF]',
      },
      {
        name: 'Fat',
        icon: 'opacity',
        consumed: actual.fat_g,
        target: Math.max(planned.fat_g, 1),
        colorClass: 'text-[#FFB300]',
        bgClass: 'bg-[rgba(255,179,0,0.12)]',
        fillClass: 'bg-[#FFB300]',
      },
    ]
  }, [todaySummary])

  const proteinGap = Math.max(0, (todaySummary?.planned.protein_g ?? 0) - (todaySummary?.actual.protein_g ?? 0))
  const mealSlots = useMemo(() => mapMealSlots(todayMeals, todayLogs), [todayMeals, todayLogs])
  const mealsDone = (todaySummary?.slots_on_plan ?? 0) + (todaySummary?.slots_substituted ?? 0)
  const mealsTotal = Math.max(
    (todaySummary?.slots_on_plan ?? 0) +
      (todaySummary?.slots_substituted ?? 0) +
      (todaySummary?.slots_skipped ?? 0) +
      (todaySummary?.slots_unlogged ?? 0),
    1,
  )

  const exercisesLogged = exercisesCompleted
  const exercisesMax = Math.max(exercisesTotal, 1)
  const exercisesOnTrack = exercisesLogged / exercisesMax >= 0.5

  const streakTitle =
    streakDays >= 7 ? 'On a roll' : streakDays >= 4 ? 'Strong week' : streakDays >= 2 ? 'Building momentum' : 'Day one counts'

  const streakMessage =
    proteinGap > 10
      ? `Hit your targets ${daysOnTarget} of ${streakDays} days. Log dinner to close today out.`
      : `${daysOnTarget} of ${streakDays} days on target. Keep it going.`

  const streakPct = Math.min(streakDays / 7, 1)
  const barTransition = reducedMotion ? 'none' : 'all 700ms'

  const navTitle = segment

  if (!mounted) return null

  return createPortal(
    <div
      className={`slide-page-panel fixed inset-0 z-[70] flex h-[100dvh] min-h-0 flex-col ${KILO.bg.page} ${
        visible ? 'slide-page-panel--open' : 'slide-page-panel--closed'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label="Tracking and progress"
    >
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-10">
        {/* Navigation bar */}
        <div className="flex items-center justify-between px-6 pb-4 pt-14">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-1 text-[17px] text-kilo-accent"
          >
            <OutlinedIcon name="arrow_back_ios" className="text-[20px] text-kilo-accent" />
            Back
          </button>
          <span className={`text-[17px] font-semibold tracking-tight ${KILO.text.primary}`}>{navTitle}</span>
          <button
            type="button"
            onClick={() => setSegment('Week')}
            className="flex items-center gap-1 text-[14px] text-kilo-accent"
          >
            Week
            <OutlinedIcon name="calendar_today" className="text-[16px] text-kilo-accent" />
          </button>
        </div>

        {/* Segment control */}
        <div className="mt-1 px-6">
          <div className={`flex gap-[2px] rounded-[10px] ${KILO.bg.nested} p-[2px]`}>
            {(['Today', 'Week', 'Month'] as Segment[]).map((label) => (
              <button
                key={label}
                type="button"
                className={`flex-1 rounded-[8px] py-[7px] text-[13px] font-medium tracking-tight transition-all duration-200 ${
                  segment === label
                    ? `${KILO.bg.card} ${KILO.text.primary} shadow-kilo-card`
                    : KILO.text.muted
                }`}
                onClick={() => setSegment(label)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Hero */}
        <div className="mt-7 flex items-start justify-between px-6">
          <div>
            <div className="mb-1.5 flex items-center gap-1.5">
              <OutlinedIcon name="bolt" className={`text-[16px] ${KILO.text.soft}`} />
              <span className={`text-[11px] font-semibold uppercase tracking-[0.5px] ${KILO.text.soft}`}>Calories</span>
            </div>
            <div className="flex items-baseline gap-1">
              <span className={`text-[52px] font-bold leading-none tracking-[-2.5px] ${KILO.text.primary}`}>
                {calories.consumed.toLocaleString()}
              </span>
              <span className={`text-[24px] font-medium tracking-[-0.8px] ${KILO.text.muted}`}>kcal</span>
            </div>
            <div className={`mt-1.5 text-[14px] tracking-tight ${KILO.text.soft}`}>
              <span className={KILO.text.muted}>
                {Math.max(0, calories.target - calories.consumed).toLocaleString()} kcal
              </span>{' '}
              to reach {segment === 'Today' ? "today's" : segment === 'Week' ? "this week's" : "this month's"} target
            </div>
          </div>
          <AdherenceRing pct={adherencePct} reducedMotion={reducedMotion} />
        </div>

        {/* Alert strip */}
        {segment === 'Today' && proteinGap > 10 ? (
          <div className="mx-6 mt-5 flex items-center gap-2.5 rounded-[12px] border border-[rgba(255,179,0,0.2)] bg-[rgba(255,179,0,0.12)] px-4 py-[13px]">
            <OutlinedIcon name="warning_amber" className="text-[16px] text-[#FFB300]" />
            <span className="flex-1 text-[14px] font-medium tracking-tight text-[#FFB300]">
              {Math.round(proteinGap)}g protein still needed today
            </span>
            <span className="flex-shrink-0 text-[13px] text-[#FFB300]/70">Fix →</span>
          </div>
        ) : null}

        {/* Macros — today only */}
        {segment === 'Today' ? (
          <>
            <SectionLabel>MACROS</SectionLabel>
            <div className={`mx-6 overflow-hidden rounded-[12px] border ${KILO.border.default} ${KILO.bg.card}`}>
              {macros.map((macro, i) => (
                <div
                  key={macro.name}
                  className={`flex items-center gap-3.5 px-[18px] py-4 ${i > 0 ? `border-t ${KILO.border.subtle}` : ''}`}
                >
                  <div
                    className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] ${macro.bgClass}`}
                  >
                    <OutlinedIcon name={macro.icon} className={`text-[18px] ${macro.colorClass}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`mb-[5px] text-[15px] font-medium tracking-tight ${KILO.text.primary}`}>{macro.name}</div>
                    <div className={`h-[4px] overflow-hidden rounded-full ${KILO.bg.nested}`}>
                      <div
                        className={`h-full rounded-full ${macro.fillClass}`}
                        style={{
                          width: `${Math.min((macro.consumed / macro.target) * 100, 100)}%`,
                          transition: barTransition,
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <div className={`text-[17px] font-semibold tracking-tight ${macro.colorClass}`}>
                      {macro.consumed}g
                    </div>
                    <div className={`mt-0.5 text-[12px] tracking-tight ${KILO.text.soft}`}>of {macro.target}g</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : null}

        {/* Activity */}
        <SectionLabel>ACTIVITY</SectionLabel>
        <div className="mt-0 flex gap-3 px-6">
          <div className={`flex flex-1 flex-col gap-2.5 rounded-[12px] border ${KILO.border.default} ${KILO.bg.card} p-4`}>
            <div className="flex items-center justify-between">
              <div className="flex h-8 w-8 items-center justify-center rounded-[9px] bg-[rgba(0,200,83,0.12)]">
                <OutlinedIcon name="fitness_center" className="text-[16px] text-[#00C853]" />
              </div>
              {exercisesOnTrack ? (
                <span className="rounded-full bg-[rgba(0,200,83,0.15)] px-2 py-[3px] text-[11px] font-semibold tracking-[0.2px] text-kilo-accent">
                  On track
                </span>
              ) : null}
            </div>
            <div>
              <div className={`text-[28px] font-bold leading-none tracking-[-1px] ${KILO.text.primary}`}>
                {exercisesLogged}
                <sup className={`align-super text-[15px] font-semibold tracking-[-0.3px] ${KILO.text.muted}`}>
                  /{exercisesMax}
                </sup>
              </div>
              <div className={`mt-[-2px] text-[12px] tracking-tight ${KILO.text.soft}`}>exercises logged</div>
            </div>
            <div className={`h-[3px] overflow-hidden rounded-full ${KILO.bg.nested}`}>
              <div
                className="h-full rounded-full bg-kilo-accent"
                style={{
                  width: `${Math.min((exercisesLogged / exercisesMax) * 100, 100)}%`,
                  transition: barTransition,
                }}
              />
            </div>
          </div>

          <div className={`flex flex-1 flex-col gap-2.5 rounded-[12px] border ${KILO.border.default} ${KILO.bg.card} p-4`}>
            <div className="flex items-center justify-between">
              <div className="flex h-8 w-8 items-center justify-center rounded-[9px] bg-[rgba(77,158,255,0.12)]">
                <OutlinedIcon name="restaurant" className="text-[16px] text-[#4D9EFF]" />
              </div>
              <span className="rounded-full bg-[rgba(77,158,255,0.12)] px-2 py-[3px] text-[11px] font-semibold tracking-[0.2px] text-[#4D9EFF]">
                {mealsDone} done
              </span>
            </div>
            <div>
              <div className={`text-[28px] font-bold leading-none tracking-[-1px] ${KILO.text.primary}`}>
                {mealsDone}
                <sup className={`align-super text-[15px] font-semibold tracking-[-0.3px] ${KILO.text.muted}`}>
                  /{mealsTotal}
                </sup>
              </div>
              <div className={`mt-[-2px] text-[12px] tracking-tight ${KILO.text.soft}`}>meals on plan</div>
            </div>
            <div className="flex gap-1.5">
              {mealSlots.map((slot) => (
                <span
                  key={slot.label}
                  className={`rounded-[6px] px-2 py-[3px] text-[11px] font-medium ${
                    slot.done ? 'bg-[rgba(0,200,83,0.10)] text-kilo-accent' : `${KILO.bg.nested} ${KILO.text.soft}`
                  }`}
                >
                  {slot.label}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Streak */}
        <div className={`mx-6 mt-3 flex items-center gap-4 rounded-[12px] border ${KILO.border.default} ${KILO.bg.card} p-[18px]`}>
          <div className="relative flex h-14 w-14 flex-shrink-0 items-center justify-center">
            <StreakRing pct={streakPct} reducedMotion={reducedMotion} />
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className={`text-[16px] font-bold leading-none tracking-tight ${KILO.text.primary}`}>{streakDays}</span>
              <span className={`mt-0.5 text-[8px] font-medium uppercase tracking-[0.4px] ${KILO.text.soft}`}>days</span>
            </div>
          </div>
          <div>
            <div className={`mb-[3px] text-[15px] font-semibold tracking-tight ${KILO.text.primary}`}>{streakTitle}</div>
            <div className={`text-[13px] leading-snug tracking-tight ${KILO.text.muted}`}>{streakMessage}</div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default TrackingProgressScreen
